import { useMemo, useState } from "react";
import ModalModern from "../ModalModern";
import ModernPopupHeader from "../common/ModernPopupHeader";
import ModernPopupBody from "../common/ModernPopupBody";
import MultiSelect from "../../forms/MultiSelect";
import ErrorLabel from "../../forms/base/ErrorLabel";
import FieldLabel from "../../forms/FieldLabel";
import NotificationAlert from "../../alerts/notifications/NotificationAlert";
import { primarySpendingCategories } from "../../../../utils/constants/plaidCategoryTaxonomy";

function CreateBudgetModal({
  onClose,
  onSave,
  metadata = [],
  apiError = "",
  isSubmitting = false,
  onClearApiError,
  copy,
}) {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    frequency: "Weekly",
    amount_limit: "",
    threshold: 90,
    institutions: [],
    accounts: [],
    categories: [],
  });

  const [categoriesError, setCategoriesError] = useState("");
  const [limitError, setLimitError] = useState("");

  const frequencyOptions = [
    { label: copy?.frequency_options?.weekly, value: "Weekly" },
    { label: copy?.frequency_options?.monthly, value: "Monthly" },
  ];

  // 1. Threshold options matching the Choice keys in your Django model
  const thresholdOptions = [
    { label: copy?.threshold_options?.fifty, value: 50 },
    { label: copy?.threshold_options?.seventy_five, value: 75 },
    { label: copy?.threshold_options?.ninety, value: 90 },
  ];

  // 2. Prepare Institution options from the real metadata
  const institutionOptions = useMemo(() => {
    return metadata.map((inst) => ({
      label: inst.institution_name,
      value: inst.uuid,
    }));
  }, [metadata]);

  // 3. Prepare Account options (Filtered by selected Institutions)
  const accountOptions = useMemo(() => {
    // Case A: No institution selected -> show ALL accounts from ALL banks
    if (formData.institutions.length === 0) {
      return metadata.flatMap((inst) =>
        inst.accounts.map((acc) => ({
          label: `${acc.name} (${inst.institution_name})`,
          value: acc.uuid,
        })),
      );
    }

    // Case B: Filter accounts belonging ONLY to selected institutions
    return metadata
      .filter((inst) => formData.institutions.includes(inst.uuid))
      .flatMap((inst) =>
        inst.accounts.map((acc) => ({
          label: acc.name,
          value: acc.uuid,
        })),
      );
  }, [metadata, formData.institutions]);

  // 4. Temporary hardcoded categories (as requested to ignore for now)
  const categoryOptions = useMemo(() => {
    return [
      { value: "ALL", label: copy?.all_categories || "All Categories" },
      ...primarySpendingCategories,
    ];
  }, [copy]);

  // Handle specific logic for Institution selection
  const handleInstitutionChange = (selectedInstUuids) => {
    setFormData((prev) => {
      // Logic: If we remove a bank, we must remove its accounts from the selection
      const allowedAccountUuids = metadata
        .filter((inst) => selectedInstUuids.includes(inst.uuid))
        .flatMap((inst) => inst.accounts.map((acc) => acc.uuid));

      const updatedAccounts = prev.accounts.filter((accUuid) =>
        allowedAccountUuids.includes(accUuid),
      );

      return {
        ...prev,
        institutions: selectedInstUuids,
        accounts: updatedAccounts,
      };
    });
  };

  const handleMultiSelectChange = (fieldName, selectedValues) => {
    if (fieldName === "institutions") {
      handleInstitutionChange(selectedValues);
    } else if (fieldName === "categories") {
      // If user clicked "All Categories", populate with all actual category values
      if (selectedValues.includes("ALL")) {
        const allCategoryValues = primarySpendingCategories.map(
          (cat) => cat.value,
        );
        setCategoriesError("");
        setFormData((prev) => ({
          ...prev,
          categories: allCategoryValues,
        }));
        return;
      }

      if (selectedValues.length > 0) {
        setCategoriesError("");
      }

      setFormData((prev) => ({
        ...prev,
        categories: selectedValues,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [fieldName]: selectedValues,
      }));
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "amount_limit") {
      if (value !== "" && Number(value) < 0) {
        return;
      }
      setLimitError("");
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFormFocus = () => {
    if (apiError) {
      onClearApiError?.();
    }
    setLimitError("");
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const limit = Number(formData.amount_limit);
    if (!Number.isNaN(limit) && limit < 0) {
      setLimitError(copy?.errors?.negative_limit);
      return;
    }

    // A budget must track at least one (spending) category.
    if (formData.categories.length === 0) {
      setCategoriesError(copy?.errors?.select_category);
      return;
    }

    onSave(formData);
  };

  return (
    <ModalModern onClose={onClose}>
      <ModernPopupHeader onClose={onClose}>
        <div className="header-content-modern-row">
          <h3 className="transaction-popup-title">{copy?.title}</h3>
        </div>
      </ModernPopupHeader>
      <ModernPopupBody>
        <form
          onSubmit={handleSubmit}
          onFocus={handleFormFocus}
          className="mp-col-container"
        >
          {apiError && (
            <NotificationAlert
              alertType="error"
              alertTitle={copy?.errors?.create_failed}
              alertDescription={apiError}
            />
          )}
          <div className="mp-col-container mobile-to-col">
            <div
              className="mp-transacion-metadata"
              style={{ width: "100%", padding: "0" }}
            >
              <div
                className="mp-row-container mobile-to-col"
                style={{ gap: "20px", padding: "0", width: "100%" }}
              >
                <div className="form-field">
                  <p>{copy?.budget_name}</p>
                  <input
                    type="text"
                    name="name"
                    className="form-input"
                    placeholder={copy?.budget_name_placeholder}
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-field">
                  <p>{copy?.frequency}</p>
                  <select
                    name="frequency"
                    className="form-input"
                    value={formData.frequency}
                    onChange={handleChange}
                  >
                    {frequencyOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form-field">
                <p>{copy?.description}</p>
                <textarea
                  name="description"
                  className="form-input"
                  placeholder={copy?.description_placeholder}
                  value={formData.description}
                  onChange={handleChange}
                  style={{ height: "80px", resize: "none" }}
                />
              </div>
              <div
                className="mp-row-container mobile-to-col"
                style={{ gap: "20px", padding: "0", width: "100%" }}
              >
                <div className="form-field">
                  <p>{copy?.limit}</p>
                  <input
                    type="number"
                    name="amount_limit"
                    className="form-input"
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                    value={formData.amount_limit}
                    onChange={handleChange}
                    required
                  />
                  {limitError && (
                    <ErrorLabel size="sm">{limitError}</ErrorLabel>
                  )}
                </div>
                <div className="form-field">
                  <p>{copy?.threshold}</p>
                  <select
                    name="threshold"
                    className="form-input"
                    value={formData.threshold}
                    onChange={handleChange}
                  >
                    {thresholdOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="mp-col-container full-width ten-gap">
                <p>{copy?.tracking_sources}</p>
                <div className="form-field">
                  <FieldLabel label={copy?.institutions} />
                  <MultiSelect
                    options={institutionOptions}
                    value={formData.institutions}
                    onChange={(values) =>
                      handleMultiSelectChange("institutions", values)
                    }
                    placeholder={copy?.select_institutions}
                  />
                </div>
                <div className="form-field">
                  <FieldLabel label={copy?.accounts} />
                  <MultiSelect
                    options={accountOptions}
                    value={formData.accounts}
                    onChange={(values) =>
                      handleMultiSelectChange("accounts", values)
                    }
                    placeholder={copy?.select_accounts}
                  />
                </div>
                <div className="form-field">
                  <FieldLabel label={copy?.categories} required />
                  <MultiSelect
                    options={categoryOptions}
                    value={formData.categories}
                    onChange={(values) =>
                      handleMultiSelectChange("categories", values)
                    }
                    placeholder={copy?.select_categories}
                  />
                  {categoriesError && (
                    <ErrorLabel size="sm">{categoriesError}</ErrorLabel>
                  )}
                </div>
              </div>
            </div>
          </div>
          <button
            type="submit"
            className="cta-button secondary"
            disabled={isSubmitting}
          >
            {isSubmitting ? copy?.creating : copy?.submit}
          </button>
        </form>
      </ModernPopupBody>
    </ModalModern>
  );
}

export default CreateBudgetModal;
